/**
 * BuildingLanding.tsx — `/building` public page.
 *
 * Teal lane only. Nav omits `/buying`. Serves both the growth-stage AI-native
 * supplier (their product IS the AI they build) and the enterprise CAIO / CISO
 * scoping internal AI governance — same register, two entry stories.
 *
 * Public, no auth. See docs/v4/launch-scope.md P0-1 partner route and
 * docs/v4/link-gaps-plan.md GAP-4.
 */

import { Link, useNavigate } from 'react-router-dom';
import { Brand, Button, marketingCopy } from '@aigovops/design-system';

const { gtmV4 } = marketingCopy;

const laneAccent = 'var(--building)';
const laneAccentSoft = 'var(--building-soft)';

export function BuildingLanding() {
  const navigate = useNavigate();
  const lane = gtmV4.lanes.building;

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
      {/* Sealed header — no `/buying` link. */}
      <header
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            padding: 'var(--space-4) var(--space-6)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
          }}
        >
          <Link
            to="/building"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              textDecoration: 'none',
              minWidth: 0,
            }}
          >
            <Brand variant="glyph" />
            <Brand variant="wordmark" />
            <span
              style={{
                marginLeft: 'var(--space-2)',
                padding: '2px var(--space-2)',
                border: `1px solid ${laneAccent}`,
                borderRadius: '999px',
                color: laneAccent,
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 700,
              }}
            >
              Building
            </span>
          </Link>
          <nav
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 'var(--space-4)',
              fontSize: 'var(--font-size-2xs)',
              fontFamily: 'var(--font-ui)',
            }}
          >
            <a href="#how" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              How it works
            </a>
            <a href="#pricing" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              Pricing
            </a>
            <Link to="/buying/samples/building-ai-register" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              Sample register
            </Link>
            <Button
              variant="primary"
              compact
              onClick={() =>
                window.open(
                  '/demo',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              Book a demo
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: laneAccent,
            fontWeight: 700,
          }}
        >
          {lane.microLabel}
        </div>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            color: 'var(--text)',
            maxWidth: '48rem',
          }}
        >
          {lane.heading}
        </h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-2)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: 700,
              lineHeight: 1,
              color: 'var(--text)',
              letterSpacing: '-0.02em',
            }}
          >
            {lane.numericAtom}
          </span>
          {lane.numericDelta ? (
            <span
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-md)',
                color: 'var(--text-muted)',
              }}
            >
              {lane.numericDelta}
            </span>
          ) : null}
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-md)',
              color: 'var(--text-muted)',
              marginLeft: 'var(--space-2)',
            }}
          >
            {lane.numericCaption}
          </span>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-md)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--line-height-body)',
            maxWidth: '52rem',
          }}
        >
          {gtmV4.whichRoom.motions[1].body}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
          <Button onClick={() => navigate('/buying/samples/building-ai-register')}>
            {lane.ctaLabel} →
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              window.open(
                '/demo',
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Book a 20-min walkthrough
          </Button>
        </div>
      </section>

      {/* Two-persona chips — same register, two entry stories */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          Same register. Two rooms.
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {PERSONAS.map((p) => (
            <article
              key={p.title}
              style={{
                padding: 'var(--space-5)',
                border: '1px solid var(--border)',
                borderLeft: `4px solid ${laneAccent}`,
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                display: 'grid',
                gap: 'var(--space-3)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: laneAccent,
                  fontWeight: 700,
                }}
              >
                {p.eyebrow}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-body)',
                }}
              >
                {p.body}
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-4)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  display: 'grid',
                  gap: 'var(--space-1)',
                }}
              >
                {p.wins.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section
        id="how"
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          How the continuous register works
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {STEPS.map((step, i) => (
            <article
              key={step.title}
              style={{
                padding: 'var(--space-5)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  color: laneAccent,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                }}
              >
                STEP {String(i + 1).padStart(2, '0')}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-body)',
                }}
              >
                {step.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Pricing rail */}
      <section
        id="pricing"
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-5)',
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 700,
              color: 'var(--text)',
            }}
          >
            Building pricing
          </h2>
          <p
            style={{
              margin: 'var(--space-2) 0 0',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              maxWidth: '52rem',
            }}
          >
            Priced aggressively for growth-stage teams — because winning ISVs pulls enterprise
            adoption. Enterprise anchor tenants pay the platform tier.
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: 'var(--space-4)',
          }}
        >
          {BUILDING_TIERS.map((tier) => (
            <article
              key={tier.name}
              style={{
                padding: 'var(--space-5)',
                border: tier.emphasized ? `1px solid ${laneAccent}` : '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                display: 'grid',
                gap: 'var(--space-3)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: tier.emphasized ? laneAccent : 'var(--text-dim)',
                  fontWeight: 700,
                }}
              >
                {tier.eyebrow}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {tier.name}
              </h3>
              <div
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'clamp(1.5rem, 2.5vw, 1.875rem)',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                {tier.price}
                <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)', fontWeight: 500, marginLeft: '4px' }}>
                  {tier.cadence}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 'var(--line-height-body)',
                }}
              >
                {tier.description}
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-4)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text)',
                  display: 'grid',
                  gap: 'var(--space-1)',
                }}
              >
                {tier.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <div style={{ marginTop: 'var(--space-2)' }}>
                <Button
                  variant={tier.emphasized ? 'primary' : 'secondary'}
                  onClick={() =>
                    window.open(
                      '/demo',
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Book a walkthrough
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Questionnaire response shortcut — same 104px section rhythm. */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
        }}
      >
        <div
          role="note"
          style={{
            padding: 'var(--space-5)',
            border: `1px solid ${laneAccent}`,
            background: laneAccentSoft,
            borderRadius: 'var(--radius-card)',
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: laneAccent,
              fontWeight: 700,
            }}
          >
            HAVE A QUESTIONNAIRE DUE FRIDAY?
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
            }}
          >
            Paste your prospect's AI security questionnaire and see gacpro render a cited draft
            response from a sample register in under a day — no full onboarding required.
          </p>
          <div>
            <Link
              to="/building/questionnaire-response"
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-sm)',
                color: laneAccent,
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
                fontWeight: 600,
              }}
            >
              Try the questionnaire-response demo →
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA — matches 104px section rhythm; no divider (rhythm is enough). */}
      <section
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          paddingTop: 'var(--space-16)',
          paddingRight: 'var(--space-6)',
          paddingBottom: 'var(--space-10)',
          paddingLeft: 'var(--space-6)',
          display: 'grid',
          gap: 'var(--space-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 700,
            color: 'var(--text)',
          }}
        >
          See the register before you commit.
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            maxWidth: '52rem',
          }}
        >
          A 20-minute walkthrough tuned to your scope — internal productivity AI, product-embedded
          AI, or both.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-2)' }}>
          <Button
            variant="primary"
            onClick={() =>
              window.open(
                '/demo',
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Book a 20-min walkthrough →
          </Button>
          <Link
            to="/buying/samples/building-ai-register"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-3)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
              color: laneAccent,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
              fontWeight: 600,
            }}
          >
            See the sample building AI register →
          </Link>
        </div>
      </section>

      <footer
        style={{
          borderTop: '1px solid var(--border)',
          padding: 'var(--space-6)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            maxWidth: '80rem',
            margin: '0 auto',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <span>© 2026 gacpro · gacpro.io · hello@gacpro.io</span>
          <span>
            <Link to="/buying/samples/building-ai-register" style={{ color: 'var(--text-dim)', marginRight: 'var(--space-3)' }}>
              Sample register
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

const PERSONAS = [
  {
    eyebrow: 'GROWTH-STAGE AI COMPANY',
    title: 'Your product IS the AI.',
    body: 'One-team, no-GRC-department companies whose engineers ship AI features to enterprise customers. Enterprise questionnaires are killing your deal cycle.',
    wins: [
      'Answer any AI questionnaire in a day, not six weeks',
      'Every answer traceable to your register',
      'SIG 2026 AI section renders on demand',
      'Growth Building tier priced for Series A-B budgets',
    ],
  },
  {
    eyebrow: 'ENTERPRISE',
    title: 'You deploy AI across dozens of teams.',
    body: 'Internal productivity AI (Copilot / Einstein), custom-built models, DIY LLM integrations, product-embedded ML. Board is asking who owns AI oversight.',
    wins: [
      'Per-system evidence trail keyed to frameworks',
      'EU AI Act deployer-role classification per system',
      'Board-reportable oversight record (SEC IAC-ready)',
      'Layer 3 customer-executive attestation',
    ],
  },
];

const STEPS = [
  {
    title: 'Attest per system',
    body: 'Every AI system you deploy or ship gets an entry — data lineage, evaluation record, named owner, framework mappings.',
  },
  {
    title: 'Sign once',
    body: 'Named system owners sign their disposition. Quarterly roll-up counter-signed by TPRM lead. GC counter-signs only material-flagged entries.',
  },
  {
    title: 'Render on demand',
    body: "Prospect questionnaire arrives — export a cited response from your register. Board meeting scheduled — export the oversight report. Auditor asks — export the framework mapping.",
  },
  {
    title: 'Refresh continuously',
    body: 'When a system changes — new evaluation, new data-boundary, new incident — the register updates and your subscribed buyers see current state without a re-survey.',
  },
];

const BUILDING_TIERS = [
  {
    eyebrow: 'Growth',
    name: 'Growth Building',
    price: '$9K',
    cadence: '/year',
    description: 'For growth-stage AI-native companies. One product, one register, questionnaire-response ready.',
    features: [
      'Continuous register for one product',
      'SIG 2026 AI + SOC 2 + AI templates',
      'Layer 3 signature (one signer)',
      'Standing AI subprocessor + disclosure page',
    ],
    emphasized: false,
  },
  {
    eyebrow: 'Growth+',
    name: 'Growth+ Building',
    price: '$32K',
    cadence: '/year',
    description: 'Multi-product teams and companies shipping AI to business + end-user customers.',
    features: [
      'Everything in Growth',
      'Buyer-subscribe network (your buyers read your register)',
      'Layer 3 counter-signature workflow',
      'Board-deliverable renderer',
      'Framework connector library',
    ],
    emphasized: true,
  },
  {
    eyebrow: 'Enterprise',
    name: 'Enterprise Building',
    price: 'Talk to us',
    cadence: '',
    description: 'Fortune-1000 CISO / CAIO scope. Cross-org rollup, SEC IAC disclosure alignment, anchor-tenant partnerships.',
    features: [
      'Everything in Growth+',
      'Cross-team + cross-org rollup',
      'Discovery-safe evidence trail',
      'Design-partner customer support',
    ],
    emphasized: false,
  },
];
